import type { SupabaseClient } from '@supabase/supabase-js';
import type { EmailCodeVerifyResponse, VerifyAuthResponse } from '@common/api/authService';
import { buildOnboardingRoutePreservingInvite } from '@/components/Onboarding/inviteRedirect';

export const EMAIL_OTP_RESEND_COOLDOWN_SECONDS = 60;

export interface SendEmailOtpParams {
    email: string;
}

export interface VerifyEmailOtpParams {
    email: string;
    token: string;
}

export interface EmailCodeAuthClient {
    sendEmailCode(email: string): Promise<unknown>;
    verifyEmailCode(email: string, code: string): Promise<EmailCodeVerifyResponse>;
}

export function getEmailOtpPostSignInRedirect(
    search: string,
    profile: Pick<VerifyAuthResponse, 'workspace'> | null,
): string | null {
    const params = new URLSearchParams(search);
    if (params.get('connect') || params.get('oauth_context')) {
        return null;
    }

    if (profile && !profile.workspace) {
        return `/admin${buildOnboardingRoutePreservingInvite(search)}`;
    }

    return decodeURIComponent(params.get('redirect') || '/admin');
}

export function getPasswordSetupRedirect(
    search: string,
    userType: 'new' | 'invited' | 'existing' | 'unknown',
): string {
    if (userType === 'new') {
        return `/admin${buildOnboardingRoutePreservingInvite(search)}`;
    }

    const params = new URLSearchParams(search);
    return decodeURIComponent(params.get('redirect') || '/admin');
}

export async function sendEmailOtp(
    authClient: EmailCodeAuthClient,
    params: SendEmailOtpParams,
) {
    await authClient.sendEmailCode(params.email);
}

export async function verifyEmailOtp(
    authClient: EmailCodeAuthClient,
    supabase: SupabaseClient,
    params: VerifyEmailOtpParams,
) {
    const session = await authClient.verifyEmailCode(params.email, params.token);
    if (!session.access_token || !session.refresh_token) {
        throw new Error('Verification succeeded without a complete session.');
    }

    const { error } = await supabase.auth.setSession({
        access_token: session.access_token,
        refresh_token: session.refresh_token,
    });

    if (error) throw error;
}
